/**
 * The register (E4, spec D24 §9.3): quote-class records on a process_engine
 * deployment, one row per record — where it is (current step), what it is
 * worth (the latest quote), who it waits on. The Status column exists only
 * when the deployment's pack names a status map; the platform has no status
 * vocabulary of its own.
 *
 * SA-462 (workflow UX sweep spec §8, D12): this is a column list over the
 * one table shell, `RegisterTable` — widths, resize, the sticky header, the
 * marked row and the footer are the shell's; the cells shared with a
 * module's view live in `registerCells.tsx`.
 * SA-1124: the stored and intake columns, Current step and Status name their
 * server sort key; SA-1183: the role columns, Route and Owner sort too, by what their cells read. SA-1141: Needed window
 * shows the window's dates (WindowCell) and sorts by them.
 */
import { useMemo, type ReactNode } from 'react'
import { QueueCell } from './queueBits'
import { timeAgo } from './queueText'
import { SourceLink } from './SourceLink'
import { sourceRefFromOrder } from './sourceText'
import { ClampedText } from './ClampedText'
import { ROLE_SLOTS, RoleValue } from './contactRoles'
import { CELL, mergeColumns, type MergedColumn, type RegisterColumn, type ServerSortColumn } from './registerColumns'
import { Dash, OwnerCell, QuoteCell, UpdateChip, StatusCell, StepCell, WindowCell } from './registerCells'
import { RegisterTable } from './RegisterTable'
import type { RegisterSort } from './registerSort'
import { intakeSlot as slot, intakeValue } from './intakeSnapshot'
import { placeCodes } from './place'
import { PlaceStations } from './PlaceStations'
import { useAirportLookup } from '@/hooks/useAirports'
import type { Airport } from '@/types/airports'
import type { Order } from '@/types/commerce'

export type { RegisterColumn } from './registerColumns'

/** The register's props. A custom module's register view takes exactly these
 * (E5 spec §7.1, `RegisterViewEntry` in `src/custom/index.ts`): the same page
 * of rows the built-in table would render. */
export interface RegisterViewProps {
  orders: Order[]
  onSelect: (order: Order) => void
  noun: { singular: string; plural: string }
  showStatus: boolean
  assigneeNames?: Record<string, string>
  /** A custom module's columns (SA-453). Absent renders the core table. */
  columns?: RegisterColumn[]
  /** The row you came back from (SA-462, D15). */
  markedRowId?: string | null
  /** The card's footer row — the pager (SA-462, §8.3). */
  footer?: ReactNode
  /** The list's sort and its setter (SA-1124): a sortable header is a sort
   *  button only when `onSort` is passed. */
  sort?: RegisterSort | null
  onSort?: (next: RegisterSort | null) => void
}

/** The core's own columns, in order, with the spec's widths (§8.4). `status`
 *  only when the deployment's pack names a status map. */
function coreColumns(
  showStatus: boolean, assigneeNames: Record<string, string>, byCode: Record<string, Airport>,
): MergedColumn[] {
  const column = (
    id: string, header: string, width: number, cellClassName: string, render: (order: Order) => ReactNode,
    extra: Partial<ServerSortColumn> = {},
  ): MergedColumn => ({ id, header, width, headerClassName: CELL, cellClassName, render, ...extra })

  return [
    column('reference', 'Reference', 200, `${CELL} font-medium text-foreground whitespace-nowrap`, (order) => (
      <>
        <span className="inline-flex items-center gap-1.5">
          {order.orderNumber}
          {/* The list carries no source probe; the order's channel names it (sourceRefFromOrder). */}
          <SourceLink source={sourceRefFromOrder(order)} />
        </span>
        <UpdateChip order={order} />
      </>
    ), { sortKey: 'reference' }),
    column('company', 'Company', 180, `${CELL} font-medium text-foreground`, (order) => {
      const company = companyOf(order)
      return company ? <ClampedText text={company} /> : <Dash />
    }, { sortKey: 'company' }),
    column('contact_name', 'Contact name', 150, `${CELL} text-foreground`, (order) => {
      const contactName = slot(order, 'contact_name')
      return contactName ? <ClampedText text={contactName} /> : null
    }, { sortKey: 'contact_name' }),
    column('contractual_partner', 'Contractual partner', 150, `${CELL} text-foreground`, (order) => (
      <RoleValue role={slot(order, ROLE_SLOTS.partner)} customer={companyOf(order)} clamp />
    ), { sortKey: 'contractual_partner' }),
    // No rule after Cargo agent (SA-468 R2): the built-in register has no
    // spanned headers, so a lone body rule read as a stray; the header's
    // resize hairlines carry the column structure. The pricing view keeps
    // its rules, where they mark groups.
    column('cargo_agent', 'Cargo agent', 150, `${CELL} text-foreground`, (order) => (
      <RoleValue role={slot(order, ROLE_SLOTS.agent)} customer={companyOf(order)} clamp />
    ), { sortKey: 'cargo_agent' }),
    column('route', 'Route', 160, `${CELL} whitespace-nowrap`, (order) => {
      if (!slot(order, 'origin') || !slot(order, 'destination')) return <Dash />
      return (
        <>
          <PlaceStations value={intakeValue(order, 'origin')} airports={byCode} compact />
          {' → '}
          <PlaceStations value={intakeValue(order, 'destination')} airports={byCode} compact />
        </>
      )
    }, { sortKey: 'route' }),
    column('needed_window', 'Needed window', 200, CELL, (order) => <WindowCell order={order} />, { sortKey: 'needed_window' }),
    column('current_step', 'Current step', 220, `${CELL} whitespace-nowrap`, (order) => <StepCell order={order} />, { sortKey: 'step' }),
    ...(showStatus
      ? [column('status', 'Status', 170, `${CELL} whitespace-nowrap`, (order) => <StatusCell order={order} />, { sortKey: 'status' })]
      : []),
    // SA-1035: who owns the record (the owner rule's name), beside the queue's
    // assignee, whose initials ride Waiting on.
    column('owner', 'Owner', 140, `${CELL} text-foreground`, (order) => <OwnerCell owner={order.owner} />, { sortKey: 'owner' }),
    column('waiting_on', 'Waiting on', 170, `${CELL} whitespace-nowrap`, (order) => (
      order.queueState ? <QueueCell order={order} assigneeNames={assigneeNames} /> : <Dash />
    ), { sortKey: 'waiting_on' }),
    column('quote', 'Quote', 160, `${CELL} whitespace-nowrap`, (order) => <QuoteCell order={order} />, { sortKey: 'quote' }),
    column('updated', 'Updated', 110, `${CELL} text-muted-foreground whitespace-nowrap`, (order) => (
      timeAgo(order.updatedAt ?? order.createdAt) || <Dash />
    ), { sortKey: 'updated' }),
  ]
}

function companyOf(order: Order): string | null {
  return slot(order, 'requesting_company') ?? order.customerName ?? null
}

export function RegisterView({
  orders, onSelect, noun, showStatus, assigneeNames = {}, columns, markedRowId = null, footer, sort, onSort,
}: RegisterViewProps) {
  // SA-1031: one lookup for the page's codes; a free-text place yields none.
  // SA-1051: every code of every place, a choice's included.
  const stationCodes = useMemo(
    () => orders.flatMap((o) => [...placeCodes(intakeValue(o, 'origin')), ...placeCodes(intakeValue(o, 'destination'))]),
    [orders],
  )
  // SA-1031: a module that replaces the Route column resolves its own codes (RouteColumns);
  // the core's lookup would be a wasted request, so it asks for nothing.
  const routeReplaced = columns?.some((c) => c.replaces === 'route') ?? false
  const { byCode } = useAirportLookup(routeReplaced ? [] : stationCodes)
  const rendered = useMemo(
    () => mergeColumns(coreColumns(showStatus, assigneeNames, byCode), columns),
    [showStatus, assigneeNames, columns, byCode],
  )
  return (
    <RegisterTable viewId="register" columns={rendered} rows={orders} onSelect={onSelect}
      ariaLabel={`${noun.plural} register`} markedRowId={markedRowId} footer={footer}
      sort={sort} onSort={onSort} />
  )
}
